/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  type CSSProperties,
  forwardRef,
  Fragment,
  type ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Logger } from '@saviynt/common';
import {
  AlertBanner,
  Box,
  type MenuInputOption,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { useFormik } from 'formik';

import { type TurnstileWidgetRef } from '../../../../shared/components';
import {
  ALIGNMENT_PROPERTY_ORIENTATION,
  DYNAMIC_ATTRIBUTE_FORM_CONFIG,
  FIELD_TYPES,
  LAYOUT_TYPES,
  NUMERIC_VALUE_VALIDATION,
  SENSITIVE_FIELD_MASK,
} from '../../../../shared/constants';
import { validationMessages } from '../../../../shared/constants/messages';
import useInvertedLocalize from '../../../../shared/hooks/useInvertedLocalize';
import { useMaxColumns } from '../../../../shared/hooks/useMaxColumns';
import {
  type BooleanField,
  type CaptchaField,
  type CheckboxField,
  type DataTableField,
  type DateField,
  type FieldElement,
  type FormValues,
  type MultiSelectField,
  type NumberField,
  type PasswordField,
  type RadioField,
  type RichTextField,
  type SensitiveField,
  type SingleSelectField,
  type TermsAndConditionsField,
  type TextareaField,
  type TextField,
} from '../../../../shared/types';
import {
  isDynamicAttributeForm as checkIsDynamicAttributeForm,
  isField,
  isSpacerField,
  isStringArray,
} from '../../../../shared/utils';
import {
  extractFormStructureForDependencyMap,
  getTroubleshootingTracker,
  trackFormRenderCompletion,
} from '../../../../shared/utils/troubleshooting';
import { isTroubleshootingEnabled } from '../../../../shared/utils/troubleshooting/storageUtils';
import {
  evaluateDateExpression,
  evaluateExpressionValue,
} from '../../../ExpressionBuilder/utils';
import BrandingLogo from '../../../FormBuilder/components/Branding/BrandingLogo/BrandingLogo';
import { DaFormProvider } from '../../context';
import {
  buildInitialValues,
  generateDynamicValidationSchemaWithMaps,
} from '../../data-layer';
import useDuplicateCheck from '../../hooks/useDuplicateCheck';
import { processDataProvidersForPreview } from '../../utils/dependencyExtractor';
import {
  computeReadOnlyStates,
  computeRequiredStates,
  computeVisibilityMaps,
  getEffectiveFieldVisibility,
  type MetadataFormProps,
  type VisibilityMaps,
} from '../../utils/previewUtils';
import {
  collectFormFieldsForVisibility,
  getClearedFieldValue,
  resolveRestoredFieldValue,
} from '../../utils/visibilityFieldValueHelpers';
import {
  BooleanSwitch,
  Captcha,
  Checkbox,
  DataTableField as DataTableFieldComponent,
  DatePicker,
  MultiSelect,
  NumberInput,
  Password,
  Radio,
  RichText,
  Sensitive,
  SingleSelect,
  Spacer,
  TermsAndConditions,
  TextAreaInput,
  TextInput,
} from '../PreviewFields';

import styles from './MetadataForm.module.css';

/**
 * Formik's setFieldValue uses lodash path parsing - a period (.) in the field name
 * is treated as nested object notation (e.g. "a.b" → values.a.b).
 * Field names with periods must use setValues to set a flat key.
 */
const FIELD_NAME_NEEDS_FLAT_SET = /[.[]/;

const parseBool = (v: unknown): boolean => {
  if (typeof v === 'boolean') {
    return v;
  }

  if (typeof v === 'number') {
    return v === 1;
  }

  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();

    return s === 'true' || s === '1';
  }

  return false;
};

// Stable empty object to prevent unnecessary re-renders
const EMPTY_DATA_PROVIDERS = {};

export interface MetadataFormRef {
  /**
   * Resets the form. When called with values, sets both current values AND initialValues,
   * making dirty=false until user actually changes something.
   * Use this when loading entity data for UPDATE forms.
   */
  resetForm: (nextState?: { values?: FormValues }) => void;
  validateAndSubmit: () => Promise<
    { ok: true } | { ok: false; errors: Record<string, unknown> }
  >;
  getFormValues: () => FormValues;
  setFormValues: (values: FormValues) => Promise<void>;
  /**
   * Returns whether the form has been modified from its initial/reset values.
   * For UPDATE forms, this indicates if user made actual changes.
   */
  isDirty: () => boolean;
  /**
   * Returns the initial values of the form.
   * Used for comparing current values against initial values to detect modifications.
   */
  getInitialValues: () => FormValues;
  /**
   * Returns the set of sensitive/password field names that the user has activated
   * (focused) during the current form session. Used by submission logic to determine
   * which sensitive fields should be included in the payload.
   */
  getActivatedSensitiveFields: () => Set<string>;
  /**
   * Returns a Set of fieldNames that are currently not visible.
   * A field is considered hidden if:
   * - It has static `isHidden: true`
   * - A HIDE/SHOW visibility expression evaluates to hide it
   * - Its parent section is hidden
   *
   * Used by submission logic to optionally exclude hidden fields from the payload
   * when `excludeHiddenFieldsOnSubmit` is enabled.
   */
  getHiddenFieldNames: () => Set<string>;
  /** Resets the Turnstile captcha widget to obtain a fresh token. */
  resetCaptcha: () => void;
}

const MetadataForm = forwardRef<MetadataFormRef, MetadataFormProps>(
  function MetadataForm(
    {
      canvasFields,
      logo = '',
      logoAlignment = ALIGNMENT_PROPERTY_ORIENTATION.CENTER,
      urlSlug = '',
      customFormUid,
      entityId,
      isDynamicAttributeForm: isDynamicAttributeFormProp,
      dataProviders = EMPTY_DATA_PROVIDERS,
      onValuesChange,
      onSubmit = () => {},
      onFormStateChange,
      showBanner,
      isPreview,
      initialData = null,
      prefilledData = null,
      isModalForm = false,
      maxColumns, // Optional override for MAX_COLUMNS
      passwordFieldRequiringConfirmation = null,
      duplicateCheckConfig,
      fieldMapping,
      isPublicForm,
      publicToken,
      category,
      captchaSiteKey,
      dataTheme,
      classNames,
      dataTestId,
      loadedFormElementsData = null,
    },
    ref
  ): ReactElement {
    const l = useInvertedLocalize(validationMessages);

    // Performance tracking refs - initialized during render for accurate timing
    const renderStartTimeRef = useRef<number>(performance.now());
    const hasTrackedRenderRef = useRef(false);

    const activatedSensitiveFieldsRef = useRef(new Set<string>());
    const clearedFieldsRef = useRef(new Set<string>());
    const captchaRef = useRef<TurnstileWidgetRef>(null);
    const prevEffectiveVisibilityRef = useRef<Map<string, boolean> | null>(
      null
    );

    const handleSentinelCleared = useCallback((fieldName: string) => {
      activatedSensitiveFieldsRef.current.add(fieldName);
    }, []);

    const entityData = initialData;
    const defaultMaxColumns = useMaxColumns();

    // Determine if this is a Dynamic Attribute form
    // MEMOIZED to avoid re-computation on every render
    // Prop takes precedence over auto-detection
    const isDaForm = useMemo(() => {
      // If explicit prop is provided, use it (override auto-detection)
      if (isDynamicAttributeFormProp !== undefined) {
        return isDynamicAttributeFormProp;
      }

      // Otherwise, auto-detect from urlSlug/customFormUid
      return checkIsDynamicAttributeForm({ urlSlug, customFormUid });
    }, [isDynamicAttributeFormProp, urlSlug, customFormUid]);

    // Determine if HTML labels should be enabled (only for DA forms)
    // MEMOIZED for performance
    const enableHtmlLabels = useMemo(
      () => isDaForm && DYNAMIC_ATTRIBUTE_FORM_CONFIG.ENABLE_HTML_LABELS,
      [isDaForm]
    );

    // Validate and compute maxColumnsValue with DA form support
    // MEMOIZED to avoid re-computation on every render
    // Priority: prop > DA form default (3) > normal form default (4)
    const maxColumnsValue = useMemo(() => {
      // Priority 1: Explicit maxColumns prop (if valid)
      if (maxColumns !== undefined && maxColumns >= 1 && maxColumns <= 10) {
        return maxColumns;
      }

      // Priority 2: DA form default (3 columns)
      if (isDaForm) {
        return DYNAMIC_ATTRIBUTE_FORM_CONFIG.MAX_COLUMNS;
      }

      // Priority 3: Normal form default from hook (4 columns)
      return defaultMaxColumns;
    }, [maxColumns, isDaForm, defaultMaxColumns]);

    // Calculate responsive column counts
    const tabletColumns = Math.min(maxColumnsValue, 2);
    const mobileColumns = 1; // Always 1 on mobile

    // Local clamping functions that respect maxColumnsValue
    // These support legacy colSpan: 4 even when maxColumnsValue < 4
    const clampColSpanLocal = (colSpan: number): number => {
      const legacyMax = Math.max(maxColumnsValue, 4); // Support legacy colSpan: 4

      return Math.max(1, Math.min(legacyMax, colSpan));
    };

    const isValidColSpanLocal = (colSpan: number): boolean => {
      const legacyMax = Math.max(maxColumnsValue, 4); // Support legacy colSpan: 4

      return colSpan >= 1 && colSpan <= legacyMax;
    };

    // Process dataProviders to extract dependencies in preview mode
    // Memoized to prevent creating new object references on every render
    const processedDataProviders = useMemo(
      () => processDataProvidersForPreview(dataProviders, isPreview ?? false),
      [dataProviders, isPreview]
    );

    // Build initial values
    // If prefilledData is provided (e.g., edit mode), merge it with default values
    const initialValues = useMemo(
      () => buildInitialValues(canvasFields, {}, prefilledData),
      [canvasFields, prefilledData]
    );

    // Create a map of fieldName to fieldType for efficient lookup
    const fieldTypeMap = useMemo(() => {
      const map = new Map<string, string>();

      const processElement = (element: FieldElement) => {
        if (element.type === 'field' && isField(element)) {
          map.set(element.fieldName, element.fieldType);
        }

        if (
          (element.type === 'section' || element.type === 'row') &&
          element.children
        ) {
          element.children.forEach(processElement);
        }
      };

      canvasFields.forEach(processElement);

      return map;
    }, [canvasFields]);

    const fieldNameToLabel = useMemo(() => {
      const labelMap: Record<string, string> = {};

      const collectLabels = (element: FieldElement) => {
        if (element.type === 'field' && isField(element)) {
          labelMap[element.fieldName] = element.label;
        }

        if (
          (element.type === 'section' || element.type === 'row') &&
          element.children
        ) {
          element.children.forEach(collectLabels);
        }
      };

      canvasFields.forEach(collectLabels);

      return labelMap;
    }, [canvasFields]);

    const [visibilityMaps, setVisibilityMaps] = useState<VisibilityMaps>(() =>
      computeVisibilityMaps(canvasFields, initialValues, {}, entityData, {
        isDaForm,
      })
    );

    const [readOnlyStates, setReadOnlyStates] = useState<
      Map<string, boolean | undefined>
    >(() => computeReadOnlyStates(canvasFields, initialValues, {}, entityData));

    const [requiredStates, setRequiredStates] = useState<
      Map<string, boolean | undefined>
    >(() => computeRequiredStates(canvasFields, initialValues, {}, entityData));
    // Track password confirmation values (keyed by fieldName for multi-password support)
    const [passwordConfirmValues, setPasswordConfirmValues] = useState<
      Record<string, string>
    >({});

    // Normalize passwordFieldRequiringConfirmation prop into an array
    const passwordFieldsRequiringConfirmation = useMemo((): PasswordField[] => {
      if (!passwordFieldRequiringConfirmation) {
        return [];
      }

      if (Array.isArray(passwordFieldRequiringConfirmation)) {
        return passwordFieldRequiringConfirmation;
      }

      return [passwordFieldRequiringConfirmation];
    }, [passwordFieldRequiringConfirmation]);

    // Lift validation schema to component state
    const [validationSchema, setValidationSchema] = useState(() =>
      generateDynamicValidationSchemaWithMaps(
        canvasFields,
        initialValues,
        {},
        visibilityMaps,
        entityData,
        l,
        isDaForm
      )
    );

    // Initialize Formik with state-managed schema
    const formik = useFormik<FormValues>({
      initialValues,
      validationSchema,
      validateOnChange: true,
      validateOnBlur: true,
      validateOnMount: true,
      onSubmit: (values) => {
        onSubmit(values);
      },
    });

    const {
      touched,
      values,
      initialValues: formikInitialValues,
      isValid,
      isValidating,
      dirty,
      errors,
      handleSubmit,
      setFieldValue,
      setFieldTouched,
      setValues,
      submitForm,
      validateForm,
      validateField,
      resetForm,
    } = formik;

    // Stable callback for Turnstile captcha token — avoids re-initialising
    // the widget on every MetadataForm render.
    const handleCaptchaToken = useCallback(
      (token: string) => {
        setFieldValue('captchaToken', token).catch(() => {});
      },
      [setFieldValue]
    );

    const handleCaptchaExpired = useCallback(() => {
      setFieldValue('captchaToken', '').catch(() => {});
    }, [setFieldValue]);

    const setFieldValueSafe = useCallback(
      (fieldName: string, value: unknown): Promise<void> => {
        if (FIELD_NAME_NEEDS_FLAT_SET.test(fieldName)) {
          return setValues({
            ...values,
            [fieldName]: value as FormValues[string],
          })
            .then(() => {})
            .catch(() => {});
        }

        return setFieldValue(fieldName, value).then(() => {});
      },
      [setFieldValue, setValues, values]
    );

    const {
      duplicateErrors,
      compositeDuplicateMessage,
      isDuplicateChecking,
      checkAllDuplicates,
    } = useDuplicateCheck({
      urlSlug,
      customFormUid,
      entityId,
      isPublic: isPublicForm,
      isPreview,
      category,
      token: publicToken,
      duplicateCheckConfig,
      fieldMapping,
      formValues: values,
      localizeError: l,
      fieldNameToLabel,
    });

    const errorsForStateChange = useMemo(
      () => ({ ...errors, ...duplicateErrors }),
      [errors, duplicateErrors]
    );

    // Set form structure for dependency map building
    useEffect(() => {
      if (canvasFields.length > 0 && Object.keys(dataProviders).length > 0) {
        try {
          // Only pass processedDataProviders if in preview mode AND it's different from saved
          // In live forms (isPreview=false), processedDataProviders === dataProviders, so no need to pass
          const shouldUseProcessed =
            isPreview && processedDataProviders !== dataProviders;

          // extractFormStructureForDependencyMap has internal unsafe assignments but is safe to call.
          // Errors are handled by the surrounding try-catch block.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
          const formStructure = extractFormStructureForDependencyMap(
            canvasFields,
            dataProviders,
            shouldUseProcessed ? processedDataProviders : undefined
          );

          const tracker = getTroubleshootingTracker();

          tracker.setFormStructure(formStructure);
        } catch (error) {
          // Silently fail - don't break form if tracking fails
          Logger.warn(
            'Failed to set form structure for dependency map:',
            error
          );
        }
      }
    }, [canvasFields, dataProviders, processedDataProviders, isPreview]);

    // Performance tracking - track form render completion
    // Uses useLayoutEffect to fire after DOM mutations but before paint
    useLayoutEffect(() => {
      // Guard 1: Only track once per mount
      if (hasTrackedRenderRef.current) {
        return;
      }

      // Guard 2: Only track if we have fields to render
      if (!canvasFields?.length) {
        return;
      }

      // Guard 3: Only track if troubleshooting is enabled
      if (!isTroubleshootingEnabled()) {
        return;
      }

      const duration = performance.now() - renderStartTimeRef.current;

      // Count only actual form fields (exclude spacers and layout elements)
      const actualFieldCount = canvasFields.filter(
        (element) => isField(element) && !isSpacerField(element)
      ).length;

      trackFormRenderCompletion(actualFieldCount, duration, urlSlug);
      hasTrackedRenderRef.current = true;
    }, [canvasFields?.length, urlSlug]);

    // Update validation schema and visibility maps when form state changes
    // Memoize computed values to prevent unnecessary re-renders
    const computedMaps = useMemo(
      () =>
        computeVisibilityMaps(canvasFields, values, touched, entityData, {
          isDaForm,
        }),
      [canvasFields, values, touched, entityData, isDaForm]
    );

    const computedReadOnlyStates = useMemo(
      () => computeReadOnlyStates(canvasFields, values, touched, entityData),
      [canvasFields, values, touched, entityData]
    );

    const computedRequiredStates = useMemo(
      () => computeRequiredStates(canvasFields, values, touched, entityData),
      [canvasFields, values, touched, entityData]
    );

    // Update state only when computed values actually change
    useEffect(() => {
      setVisibilityMaps(computedMaps);
      setReadOnlyStates(computedReadOnlyStates);
      setRequiredStates(computedRequiredStates);

      const newSchema = generateDynamicValidationSchemaWithMaps(
        canvasFields,
        values,
        touched,
        {
          fieldVisibility: computedMaps.fieldVisibility,
          sectionVisibility: computedMaps.sectionVisibility,
          fieldToSection: computedMaps.fieldToSection,
        },
        entityData,
        l,
        isDaForm
      );

      setValidationSchema(newSchema);
    }, [
      computedMaps,
      computedReadOnlyStates,
      computedRequiredStates,
      canvasFields,
      values,
      touched,
      entityData,
      l,
      isDaForm,
    ]);

    // Evaluate expression-based default values and update Formik
    useEffect(() => {
      const updates: Record<string, unknown> = {};
      let hasUpdates = false;

      canvasFields.forEach((element) => {
        const processField = (field: FieldElement) => {
          if (
            field.type === 'field' &&
            getEffectiveFieldVisibility(field.id, computedMaps) &&
            !touched[field.fieldName] &&
            !clearedFieldsRef.current.has(field.fieldName) &&
            field.defaultValue?.isExpression
          ) {
            let evaluatedValue: unknown;
            const defaultValue = field.defaultValue.value;

            if (field.fieldType === FIELD_TYPES.DATE) {
              evaluatedValue = evaluateDateExpression(defaultValue, {
                values,
                entityData,
              });
            } else {
              evaluatedValue = evaluateExpressionValue(defaultValue, {
                values,
                touched,
                entityData,
              });
            }

            // Only update if different from current value
            // Special handling for Date objects - compare by timestamp
            const shouldUpdate =
              evaluatedValue instanceof Date &&
              values[field.fieldName] instanceof Date
                ? (values[field.fieldName] as Date).getTime() !==
                  evaluatedValue.getTime()
                : values[field.fieldName] !== evaluatedValue;

            if (shouldUpdate) {
              updates[field.fieldName] = evaluatedValue;
              hasUpdates = true;
            }
          }

          // Process children recursively
          if (
            (field.type === 'section' || field.type === 'row') &&
            field.children
          ) {
            field.children.forEach(processField);
          }
        };

        processField(element);
      });

      if (hasUpdates) {
        setValues({ ...values, ...updates } as FormValues).catch(() => {});
      }
    }, [canvasFields, computedMaps, values, touched, setValues, entityData]);

    useEffect(() => {
      prevEffectiveVisibilityRef.current = null;
    }, [canvasFields]);

    useEffect(() => {
      const maps = computedMaps;
      const fields = collectFormFieldsForVisibility(canvasFields);
      const nextMap = new Map<string, boolean>();

      fields.forEach((f) => {
        nextMap.set(f.id, getEffectiveFieldVisibility(f.id, maps));
      });

      const prev = prevEffectiveVisibilityRef.current;

      prevEffectiveVisibilityRef.current = nextMap;

      if (!prev) {
        return;
      }

      const updates: Record<string, unknown> = {};
      const touchedClears: string[] = [];
      const confirmClears: string[] = [];

      const restoreCtx = {
        values,
        touched,
        entityData,
      };

      fields.forEach((field) => {
        const was = prev.get(field.id);
        const now = nextMap.get(field.id);

        if (was === undefined || now === undefined) {
          return;
        }

        if (was && !now) {
          updates[field.fieldName] = getClearedFieldValue(field);
          touchedClears.push(field.fieldName);

          if (field.fieldType === FIELD_TYPES.DATE) {
            clearedFieldsRef.current.add(field.fieldName);
          }

          if (
            field.fieldType === FIELD_TYPES.PASSWORD &&
            passwordFieldsRequiringConfirmation.some(
              (pw) => pw.fieldName === field.fieldName
            )
          ) {
            confirmClears.push(field.fieldName);
          }

          if (
            field.fieldType === FIELD_TYPES.SENSITIVE ||
            field.fieldType === FIELD_TYPES.PASSWORD
          ) {
            activatedSensitiveFieldsRef.current.delete(field.fieldName);
          }
        } else if (!was && now) {
          const restored = resolveRestoredFieldValue(
            field,
            formikInitialValues,
            restoreCtx
          );

          updates[field.fieldName] = restored;
          touchedClears.push(field.fieldName);

          clearedFieldsRef.current.delete(field.fieldName);

          if (restored === SENSITIVE_FIELD_MASK) {
            activatedSensitiveFieldsRef.current.delete(field.fieldName);
          }
        }
      });

      if (Object.keys(updates).length === 0) {
        return;
      }

      setValues({ ...values, ...updates } as FormValues, false)
        .then(() => {
          touchedClears.forEach((fn) => {
            setFieldTouched(fn, false, false).catch(() => {});
          });
        })
        .catch(() => {});

      if (confirmClears.length > 0) {
        setPasswordConfirmValues((prevPw) => {
          const nextPw = { ...prevPw };

          confirmClears.forEach((fn) => {
            nextPw[fn] = '';
          });

          return nextPw;
        });
      }
    }, [
      computedMaps,
      canvasFields,
      values,
      touched,
      entityData,
      formikInitialValues,
      setValues,
      setFieldTouched,
      passwordFieldsRequiringConfirmation,
    ]);

    useEffect(() => {
      validateForm().catch(() => {});
    }, [validationSchema]);

    // Handle prefilledData changes (e.g., when BEC loads edit mode data)
    // Reset form with new initialValues and mark prefilled fields as touched
    useEffect(() => {
      if (prefilledData) {
        // Reset form with the new initial values
        resetForm({ values: initialValues });

        // Mark non-empty fields as touched to prevent expression defaults from overwriting
        markNonEmptyFieldsAsTouched(initialValues, false).catch(() => {});
      }
      // Only run when prefilledData changes, not when helper functions change
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefilledData]);

    // Helper to convert string dates to Date objects for date fields
    const convertDateStringsToDateObjects = (
      valuesToConvert: FormValues
    ): FormValues => {
      const processedValues: FormValues = { ...valuesToConvert };

      Object.keys(processedValues).forEach((fieldName) => {
        const fieldType = fieldTypeMap.get(fieldName);
        const value = processedValues[fieldName];

        // If field is a date type and value is a string, convert to Date
        if (fieldType === FIELD_TYPES.DATE && typeof value === 'string') {
          const dateValue = new Date(value);

          // Only set if valid date
          if (!Number.isNaN(dateValue.getTime())) {
            processedValues[fieldName] = dateValue;
          }
        }
      });

      return processedValues;
    };

    // Helper to mark non-empty prefilled fields as touched to prevent
    // expression-based defaultValue from overwriting them.
    const markNonEmptyFieldsAsTouched = (
      valuesToMark: FormValues,
      shouldValidate = true
    ): Promise<void> => {
      const fieldsToTouch = Object.keys(valuesToMark).filter((key) => {
        const value = valuesToMark[key];

        // Mark as touched if value exists and is not empty
        return (
          value !== undefined &&
          value !== null &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0)
        );
      });

      if (fieldsToTouch.length === 0) {
        return Promise.resolve();
      }

      return Promise.all(
        fieldsToTouch.map((fieldName) =>
          setFieldTouched(fieldName, true, shouldValidate)
        )
      )
        .then(() => {})
        .catch(() => {});
    };

    // Expose reset and validation functions to parent
    useImperativeHandle(ref, () => ({
      resetForm: (nextState?: { values?: FormValues }) => {
        activatedSensitiveFieldsRef.current.clear();
        clearedFieldsRef.current.clear();

        if (nextState?.values) {
          // Convert string dates to Date objects before resetting
          const processedValues = convertDateStringsToDateObjects(
            nextState.values
          );

          resetForm({ values: processedValues });

          // Mark non-empty prefilled fields as touched (skip validation)
          markNonEmptyFieldsAsTouched(processedValues, false).catch(() => {});
        } else {
          resetForm();
        }
      },
      validateAndSubmit: async () => {
        const validationErrors = await validateForm();

        // Check ALL password fields that require confirmation.
        // Skip check when the password still holds the backend sentinel —
        // the user hasn't touched it, so there's nothing to confirm.
        const hasPasswordConfirmError =
          passwordFieldsRequiringConfirmation.some((pwField) => {
            const isSentinel =
              values[pwField.fieldName] === SENSITIVE_FIELD_MASK;

            if (isSentinel) {
              return false;
            }

            const confirmVal = passwordConfirmValues[pwField.fieldName] ?? '';

            return !confirmVal || confirmVal !== values[pwField.fieldName];
          });

        const hasFormikErrors =
          Object.keys(validationErrors).length > 0 || hasPasswordConfirmError;

        if (hasFormikErrors) {
          await Promise.all(
            Object.keys(validationErrors).map((fn) => setFieldTouched(fn, true))
          );

          return { ok: false, errors: validationErrors };
        }

        // Run pre-submit duplicate check
        const { singleErrors, compositeMessage } = await checkAllDuplicates();

        const hasDuplicateErrors = Object.keys(singleErrors).length > 0;

        if (hasDuplicateErrors || compositeMessage) {
          if (hasDuplicateErrors) {
            await Promise.all(
              Object.keys(singleErrors).map((fn) => setFieldTouched(fn, true))
            );
          }

          return {
            ok: false,
            errors: { ...validationErrors, ...singleErrors },
          };
        }

        await submitForm();

        return { ok: true };
      },
      getFormValues: () => values,
      setFormValues: async (newValues: FormValues) => {
        // Convert string dates to Date objects for date fields
        const processedValues = convertDateStringsToDateObjects(newValues);

        // Merge with current values so partial updates don't wipe
        // fields that the caller didn't include (e.g. default values
        // for checkbox/date fields that the consumer doesn't know about).
        const mergedValues: FormValues = { ...values, ...processedValues };

        await setValues(mergedValues).catch(() => {
          Logger.error('Error setting values');
        });

        // Mark non-empty prefilled fields as touched (with validation)
        await markNonEmptyFieldsAsTouched(mergedValues);
      },
      isDirty: () => dirty,
      getInitialValues: () => formik.initialValues,
      getActivatedSensitiveFields: () => activatedSensitiveFieldsRef.current,
      getHiddenFieldNames: () => {
        const hiddenNames = new Set<string>();

        const collectHidden = (elements: FieldElement[]) => {
          elements.forEach((element) => {
            if (element.type === 'field') {
              const fieldVisible =
                visibilityMaps.fieldVisibility.get(element.id) ?? true;
              const sectionId = visibilityMaps.fieldToSection.get(element.id);
              const sectionVisible = sectionId
                ? (visibilityMaps.sectionVisibility.get(sectionId) ?? true)
                : true;

              if (!fieldVisible || !sectionVisible) {
                hiddenNames.add(element.fieldName);
              }
            }

            if (
              (element.type === 'section' || element.type === 'row') &&
              element.children
            ) {
              collectHidden(element.children);
            }
          });
        };

        collectHidden(canvasFields);

        return hiddenNames;
      },
      resetCaptcha: () => {
        captchaRef.current?.reset();
      },
    }));

    // Notify parent of value changes
    useEffect(() => {
      if (onValuesChange) {
        onValuesChange(values);
      }
    }, [values, onValuesChange]);

    useEffect(() => {
      if (!onFormStateChange) {
        return;
      }

      // Check ALL password fields that require confirmation.
      // Skip check when sentinel is still active (user hasn't touched the field).
      const hasPasswordConfirmError = passwordFieldsRequiringConfirmation.some(
        (pwField) => {
          const isSentinel = values[pwField.fieldName] === SENSITIVE_FIELD_MASK;

          if (isSentinel) {
            return false;
          }

          const confirmVal = passwordConfirmValues[pwField.fieldName] ?? '';

          return !confirmVal || confirmVal !== values[pwField.fieldName];
        }
      );

      const hasDuplicateErrors = Object.keys(duplicateErrors).length > 0;

      onFormStateChange({
        isSubmittable:
          isValid &&
          !isValidating &&
          !hasPasswordConfirmError &&
          !hasDuplicateErrors &&
          !isDuplicateChecking,
        isValidating: isValidating || isDuplicateChecking,
        dirty,
        errors: errorsForStateChange,
      });
    }, [
      isValid,
      dirty,
      errorsForStateChange,
      onFormStateChange,
      isValidating,
      isDuplicateChecking,
      duplicateErrors,
      passwordConfirmValues,
      passwordFieldsRequiringConfirmation,
      values,
    ]);

    const getFieldError = (fieldName: string): string | undefined => {
      if (touched[fieldName] && errors[fieldName]) {
        return String(errors[fieldName]);
      }

      if (duplicateErrors[fieldName]) {
        return String(duplicateErrors[fieldName]);
      }

      return undefined;
    };

    /**
     * Renders individual form fields based on their type using visibility maps
     */
    const renderField = (field: FieldElement): ReactElement | null => {
      const { id, type, label, fieldName } = field;

      if (type === LAYOUT_TYPES.ROW) {
        const children = field.children ?? [];
        const visibleChildren = children.filter(
          (child) => visibilityMaps.fieldVisibility.get(child.id) === true
        );

        if (visibleChildren.length === 0) {
          return null;
        }

        return (
          <Box
            key={id}
            className={cx(styles?.row, classNames?.row)}
            style={
              {
                '--max-columns': maxColumnsValue,
                '--tablet-columns': tabletColumns,
                '--mobile-columns': mobileColumns,
              } as CSSProperties & {
                '--max-columns': number;
                '--tablet-columns': number;
                '--mobile-columns': number;
              }
            }>
            {visibleChildren.map((child) => {
              // For DA forms, password fields with confirmation need full row width
              const isDAPassword =
                isDaForm && child.fieldType === FIELD_TYPES.PASSWORD;
              const rawColSpan = isDAPassword ? 2 : child.colSpan || 1;

              // Use local clamping function (respects maxColumnsValue)
              const colSpan = clampColSpanLocal(rawColSpan);

              // Clamp to maxColumnsValue for CSS class generation
              // This prevents overflow: colSpan: 4 in 3-column grid would overflow
              const clampedColSpan = Math.min(colSpan, maxColumnsValue);

              // Generate class name dynamically
              let colSpanClass: string | null = null;

              if (
                isValidColSpanLocal(clampedColSpan) &&
                clampedColSpan >= 1 &&
                clampedColSpan <= 4
              ) {
                colSpanClass =
                  styles?.[`fieldWrapperColSpan${clampedColSpan}`] ?? null;
              }

              // Fallback: if invalid or > 4, use colSpan4 (legacy support)
              if (!colSpanClass && colSpan > 4) {
                colSpanClass = styles?.fieldWrapperColSpan4 ?? null;
              }

              // Final fallback: use colSpan1 if still no class
              if (!colSpanClass) {
                colSpanClass = styles?.fieldWrapperColSpan1 ?? null;
              }

              return (
                <Box
                  key={child.id}
                  className={cx(styles?.fieldWrapper, colSpanClass)}>
                  {renderField(child)}
                </Box>
              );
            })}
          </Box>
        );
      }

      if (type === LAYOUT_TYPES.SECTION) {
        // Check if section itself is visible
        const sectionVisible = visibilityMaps.sectionVisibility.get(id) ?? true;

        if (!sectionVisible) {
          return null;
        }

        // Check if section has any visible content
        const hasVisibleContent =
          visibilityMaps.sectionHasVisibleFields.get(id) ?? false;

        if (!hasVisibleContent) {
          return null;
        }

        const children = field.children ?? [];

        return (
          <Box key={id} className={cx(styles?.section, classNames?.section)}>
            {label && (
              <Box className={styles?.sectionHeading}>
                <Typography kind='h4'>{label}</Typography>
              </Box>
            )}
            <Box
              className={cx(styles?.sectionFields, classNames?.sectionFields)}>
              {children.map((child) => renderField(child))}
            </Box>
          </Box>
        );
      }

      if (field.fieldType === FIELD_TYPES.TEXT) {
        const fieldValue: string = values[fieldName] as string;

        const fieldError = getFieldError(fieldName);

        const isDynamicRequired = requiredStates.get(field.id);

        const isDynamicReadOnly = readOnlyStates.get(field.id);

        return (
          <TextInput
            key={id}
            field={field as TextField}
            value={fieldValue}
            isRequired={isDynamicRequired}
            isReadOnly={isDynamicReadOnly}
            onChange={(value) => {
              setFieldValueSafe(fieldName, value)
                .then(() => {
                  validateField(fieldName).catch(() => {});
                })
                .catch(() => {});
              setFieldTouched(fieldName, true).catch(() => {});
            }}
            error={fieldError}
          />
        );
      }

      if (field.fieldType === FIELD_TYPES.TEXTAREA) {
        const fieldValue: string = values[fieldName] as string;

        const fieldError =
          touched[fieldName] && errors[fieldName]
            ? String(errors[fieldName])
            : undefined;

        const isDynamicRequired = requiredStates.get(field.id);

        const isDynamicReadOnly = readOnlyStates.get(field.id);

        return (
          <TextAreaInput
            key={id}
            field={field as TextareaField}
            value={fieldValue}
            isRequired={isDynamicRequired}
            isReadOnly={isDynamicReadOnly}
            onChange={(event) => {
              const newValue = event.target.value;

              setFieldValueSafe(fieldName, newValue)
                .then(() => {
                  validateField(fieldName).catch(() => {});
                })
                .catch(() => {});
              setFieldTouched(fieldName, true).catch(() => {});
            }}
            error={fieldError}
          />
        );
      }

      if (field.fieldType === FIELD_TYPES.SINGLESELECT) {
        const fieldValue = values[fieldName] as unknown;
        const singleSelectField = field as SingleSelectField;
        const dataProvider =
          processedDataProviders[singleSelectField.dataProviderId];

        if (!dataProvider) {
          return null;
        }

        const fieldError =
          touched[fieldName] && errors[fieldName]
            ? String(errors[fieldName])
            : undefined;

        const isDynamicRequired = requiredStates.get(field.id);

        const isDynamicReadOnly = readOnlyStates.get(field.id);

        return (
          <SingleSelect
            field={field as SingleSelectField}
            urlSlug={urlSlug}
            customFormUid={customFormUid}
            value={fieldValue as string | MenuInputOption | null}
            error={fieldError}
            isRequired={isDynamicRequired}
            isReadOnly={isDynamicReadOnly}
            dataProvider={dataProvider}
            onChange={(selectedOption) => {
              setFieldValueSafe(fieldName, selectedOption)
                .then(() => validateField(fieldName).catch(() => {}))
                .catch(() => {});
              setFieldTouched(fieldName, true).catch(() => {});
            }}
            canvasFields={canvasFields}
            dataProviders={processedDataProviders}
            formValues={values}
            isPreview={isPreview}
            isModalForm={isModalForm}
            initialData={entityData}
            isPublicForm={isPublicForm}
            entityId={entityId}
            fieldTypeMap={fieldTypeMap}
            loadedFormElementsData={loadedFormElementsData}
          />
        );
      }

      if (field.fieldType === FIELD_TYPES.RADIO) {
        const radioField = field as RadioField;
        const rawValue = values[fieldName];
        const dataProvider = processedDataProviders[radioField.dataProviderId];

        if (!dataProvider) {
          return null;
        }

        let selected: string | null = null;

        if (Array.isArray(rawValue) && rawValue.length > 0) {
          const firstItem = rawValue[0];

          selected = typeof firstItem === 'string' ? firstItem : null;
        } else if (typeof rawValue === 'string') {
          selected = rawValue;
        }

        const isDynamicRequired = requiredStates.get(field.id);

        const isDynamicReadOnly = readOnlyStates.get(field.id);

        return (
          <Radio
            key={id}
            field={radioField}
            radioSelection={selected}
            isRequired={isDynamicRequired}
            isReadOnly={isDynamicReadOnly}
            onChange={(val: string) => {
              // store as [value]
              setFieldValueSafe(field.fieldName, val).catch(() => {});
            }}
            error={
              touched[field.fieldName] && errors[field.fieldName]
                ? String(errors[field.fieldName])
                : undefined
            }
            dataProvider={dataProvider}
          />
        );
      }

      if (field.fieldType === FIELD_TYPES.MULTISELECT) {
        const raw = values[fieldName] as unknown;
        const multiSelectField = field as MultiSelectField;
        const fieldValue = raw;

        const dataProvider =
          processedDataProviders[multiSelectField.dataProviderId];

        if (!dataProvider) {
          return null;
        }

        const fieldError =
          touched[fieldName] && errors[fieldName]
            ? String(errors[fieldName])
            : undefined;

        const isDynamicRequired = requiredStates.get(field.id);

        const isDynamicReadOnly = readOnlyStates.get(field.id);

        return (
          <MultiSelect
            field={field as MultiSelectField}
            value={fieldValue as string[] | MenuInputOption[] | null}
            urlSlug={urlSlug}
            customFormUid={customFormUid}
            error={fieldError}
            isRequired={isDynamicRequired}
            isReadOnly={isDynamicReadOnly}
            dataProvider={dataProvider}
            onChange={(selectedOptions) => {
              setFieldValueSafe(fieldName, selectedOptions)
                .then(() => {
                  validateField(fieldName).catch(() => {});
                })
                .catch(() => {});
              setFieldTouched(fieldName, true).catch(() => {});
            }}
            canvasFields={canvasFields}
            formValues={values}
            isPreview={isPreview}
            isModalForm={isModalForm}
            initialData={entityData}
            isPublicForm={isPublicForm}
            entityId={entityId}
            fieldTypeMap={fieldTypeMap}
            loadedFormElementsData={loadedFormElementsData}
          />
        );
      }

      if (field.fieldType === FIELD_TYPES.DATATABLE) {
        return (
          <DataTableFieldComponent
            key={id}
            field={field as DataTableField}
            dataProviders={processedDataProviders}
            customFormUid={customFormUid}
            isPublicForm={isPublicForm}
          />
        );
      }

      if (field.fieldType === FIELD_TYPES.CHECKBOX) {
        const checkboxField = field as CheckboxField;
        const raw = values[fieldName] as unknown;
        const dataProvider =
          processedDataProviders[checkboxField.dataProviderId];

        if (!dataProvider) {
          return null;
        }

        let fieldValue: string[] = [];

        if (isStringArray(raw)) {
          fieldValue = raw;
        } else if (typeof raw === 'string') {
          fieldValue = raw
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v.length > 0);
        }

        const fieldError =
          touched[fieldName] && errors[fieldName]
            ? String(errors[fieldName])
            : undefined;

        const isDynamicRequired = requiredStates.get(field.id);

        const isDynamicReadOnly = readOnlyStates.get(field.id);

        return (
          <Checkbox
            key={id}
            field={checkboxField}
            value={fieldValue}
            error={fieldError}
            isRequired={isDynamicRequired}
            isReadOnly={isDynamicReadOnly}
            dataProvider={dataProvider}
            onChange={(newValues) => {
              setFieldValueSafe(fieldName, newValues).catch(() => {});
              setFieldTouched(fieldName, true).catch(() => {});
            }}
          />
        );
      }

      if (field.fieldType === FIELD_TYPES.SPACER && isSpacerField(field)) {
        return <Spacer key={field.id} field={field} />;
      }

      if (field.fieldType === FIELD_TYPES.DATE) {
        const dateField = field as DateField;
        // Use current value, or evaluate default value expression if no current value
        const fieldValue: Date | null = values[fieldName] as Date;

        const fieldError =
          touched[fieldName] && errors[fieldName]
            ? String(errors[fieldName])
            : undefined;

        const isDynamicRequired = requiredStates.get(field.id);

        const isDynamicReadOnly = readOnlyStates.get(field.id);

        return (
          <DatePicker
            key={id}
            field={dateField}
            value={fieldValue}
            error={fieldError}
            isRequired={isDynamicRequired}
            isReadOnly={isDynamicReadOnly}
            isPreview={isPreview}
            isModalForm={isModalForm}
            onChange={(date) => {
              if (date === null) {
                clearedFieldsRef.current.add(fieldName);
              } else {
                clearedFieldsRef.current.delete(fieldName);
              }

              setFieldTouched(fieldName, true, false).catch(() => {});
              setFieldValueSafe(fieldName, date)
                .then(() => {
                  validateField(fieldName).catch(() => {});
                })
                .catch(() => {});
            }}
          />
        );
      }

      if (field.fieldType === FIELD_TYPES.NUMBER) {
        const fieldValue: string = values[fieldName] as string;

        const fieldError = getFieldError(fieldName);

        const isDynamicRequired = requiredStates.get(field.id);

        const isDynamicReadOnly = readOnlyStates.get(field.id);

        return (
          <NumberInput
            key={id}
            field={field as NumberField}
            value={fieldValue}
            isRequired={isDynamicRequired}
            isReadOnly={isDynamicReadOnly}
            onChange={(value) => {
              const trimmed = value.trim();
              const num = Number(trimmed);
              const isOverLimit =
                trimmed.length > NUMERIC_VALUE_VALIDATION.MAX_CHARS;
              const isInfinite = !Number.isFinite(num);
              const storedValue =
                trimmed === '' || isOverLimit || isInfinite ? trimmed : num;

              setFieldValueSafe(
                fieldName,
                storedValue === '' ? '' : storedValue
              )
                .then(() => {
                  validateField(fieldName).catch(() => {});
                })
                .catch(() => {});
              setFieldTouched(fieldName, true).catch(() => {});
            }}
            error={fieldError}
          />
        );
      }

      if (field.fieldType === FIELD_TYPES.BOOLEAN) {
        const boolField = field as BooleanField;
        const raw = values[fieldName];

        const fieldValue =
          raw === null || raw === undefined ? null : parseBool(raw);

        const fieldError =
          touched[fieldName] && errors[fieldName]
            ? String(errors[fieldName])
            : undefined;

        const isDynamicRequired = requiredStates.get(field.id);

        const isDynamicReadOnly = readOnlyStates.get(field.id);

        return (
          <BooleanSwitch
            key={id}
            field={boolField}
            isChecked={fieldValue}
            isRequired={isDynamicRequired}
            isReadOnly={isDynamicReadOnly}
            error={fieldError}
            onChange={(next: boolean) => {
              setFieldValueSafe(fieldName, next)
                .then(() => {
                  setFieldTouched(fieldName, true).catch(() => {});
                  validateField(fieldName).catch(() => {});
                })
                .catch(() => {});
            }}
          />
        );
      }

      if (field.fieldType === FIELD_TYPES.SENSITIVE) {
        const fieldValue: string = values[fieldName] as string;

        const fieldError =
          touched[fieldName] && errors[fieldName]
            ? String(errors[fieldName])
            : undefined;

        const isDynamicRequired = requiredStates.get(field.id);

        return (
          <Sensitive
            key={id}
            field={field as SensitiveField}
            value={fieldValue}
            isRequired={isDynamicRequired}
            onChange={(value) => {
              setFieldValueSafe(fieldName, value)
                .then(() => {
                  validateField(fieldName).catch(() => {});
                })
                .catch(() => {});
              setFieldTouched(fieldName, true).catch(() => {});
            }}
            error={fieldError}
            onSentinelCleared={handleSentinelCleared}
          />
        );
      }

      if (field.fieldType === FIELD_TYPES.PASSWORD) {
        const passwordField = field as PasswordField;

        // For DA forms, enable confirmation and auto-generation by default
        const effectiveAdvConfig = isDaForm
          ? {
              ...passwordField.advConfig,
              isPasswordConfirmation:
                passwordField.advConfig?.isPasswordConfirmation ?? true,
              isAutoGenerationEnabled:
                passwordField.advConfig?.isAutoGenerationEnabled ?? true,
            }
          : passwordField.advConfig;
        const effectivePasswordField = isDaForm
          ? {
              ...passwordField,
              advConfig: effectiveAdvConfig,
              // DA password fields span full row width to fit confirmation input
              colSpan: 2,
            }
          : passwordField;

        const fieldValue: string = (values[fieldName] as string) || '';
        const fieldError =
          touched[fieldName] && errors[fieldName]
            ? String(errors[fieldName])
            : undefined;

        const isDynamicRequired = requiredStates.get(field.id);
        const needsConfirmation =
          effectivePasswordField.advConfig?.isPasswordConfirmation ?? false;

        return (
          <Password
            key={id}
            field={effectivePasswordField}
            value={fieldValue}
            isRequired={isDynamicRequired}
            onChange={(value) => {
              setFieldValueSafe(fieldName, value)
                .then(() => {
                  validateField(fieldName).catch(() => {});
                })
                .catch(() => {});
              setFieldTouched(fieldName, true).catch(() => {});
            }}
            error={fieldError}
            confirmValue={
              needsConfirmation
                ? (passwordConfirmValues[fieldName] ?? '')
                : undefined
            }
            onConfirmChange={
              needsConfirmation
                ? (v: string) =>
                    setPasswordConfirmValues((prev) => ({
                      ...prev,
                      [fieldName]: v,
                    }))
                : undefined
            }
            onSentinelCleared={handleSentinelCleared}
          />
        );
      }

      if (field.fieldType === FIELD_TYPES.RICHTEXT) {
        const richTextField = field as RichTextField;

        return <RichText key={field.id} field={richTextField} />;
      }

      if (field.fieldType === FIELD_TYPES.TERMSANDCONDITIONS) {
        const termsField = field as TermsAndConditionsField;
        const raw = values[fieldName];
        const fieldValue = parseBool(raw);
        const fieldError =
          touched[fieldName] && errors[fieldName]
            ? String(errors[fieldName])
            : undefined;
        const isDynamicRequired = requiredStates.get(field.id);
        const isDynamicReadOnly = readOnlyStates.get(field.id);

        return (
          <TermsAndConditions
            key={field.id}
            field={termsField}
            isChecked={fieldValue}
            onChange={(checked) => {
              setFieldValueSafe(fieldName, checked)
                .then(() => {
                  setFieldTouched(fieldName, true).catch(() => {});
                  validateField(fieldName).catch(() => {});
                })
                .catch(() => {});
            }}
            error={fieldError}
            isRequired={isDynamicRequired}
            isReadOnly={isDynamicReadOnly}
            showBanner={showBanner}
          />
        );
      }

      if (field.fieldType === FIELD_TYPES.CAPTCHA) {
        const captchaField = field as CaptchaField;

        return (
          <Captcha
            ref={captchaRef}
            key={field.id}
            field={captchaField}
            siteKey={!isPreview ? captchaSiteKey : undefined}
            onToken={!isPreview ? handleCaptchaToken : undefined}
            onExpired={!isPreview ? handleCaptchaExpired : undefined}
            isPreview={isPreview}
          />
        );
      }

      return null;
    };

    return (
      <DaFormProvider isDaForm={isDaForm} enableHtmlLabels={enableHtmlLabels}>
        <form
          onSubmit={handleSubmit}
          data-theme={dataTheme}
          className={cx(styles?.root, classNames?.root)}
          data-testid={dataTestId}>
          {/* Logo */}
          {logo && (
            <Box className={cx(styles?.logoWrapper, classNames?.logoWrapper)}>
              <BrandingLogo
                logo={logo}
                logoAlignment={logoAlignment}
                classNames={{ logoImg: classNames?.logoImg }}
              />
            </Box>
          )}
          {/* Composite duplicate banner */}
          <AlertBanner
            colorTheme='Critical'
            title=''
            description={compositeDuplicateMessage ?? ''}
            isVisible={Boolean(compositeDuplicateMessage)}
            onCancel={() => {}}
            isCloseButtonVisible={false}
            className={styles?.compositeDuplicateBanner}
          />
          {/* Fields map */}
          <Box className={styles?.fieldsMap}>
            {canvasFields?.map((field) => (
              <Fragment key={field.id}>{renderField(field)}</Fragment>
            ))}
          </Box>
        </form>
      </DaFormProvider>
    );
  }
);

export default MetadataForm;
